import React, { useContext } from "react";
import { Link, withRouter } from "react-router-dom";
import styled, { css } from "styled-components";
import AppleImg from "src/img/new/apple.svg";
import GoogleImg from "src/img/new/Google.svg";
import MeBusinessLogo from "../img/new/me_logo.svg";
import { tablet, desktop } from "./Media";
import { IsFeatureReady } from "src/constants/features";
import { useLocation } from "react-router-dom";
import MeBusinessPortalLogo from "./MeBusinessPortalLogo";
import { UserKeys } from "src/constants/userDetails";
import { UserContext } from "src/scenes/App/UserContext";
import { getAppDetails } from "src/config/appVariants";

// Styled components

const HeaderContainer = styled.header`
    flex: 0;
    top: ${p => p.top};
    transition: background-color 0.3s;
    width: 100%;
    margin-bottom: ${ p => p.isGuest && "160px"};
    position: relative;
    z-index: 10;
`;

const Content = styled.div`
    position: fixed;
    z-index: 10;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    min-height: 64px;
    text-align: center;
    background: ${ p => p.backgroundColor || "rgba(11, 13, 21, 0.88)"};
    backdrop-filter: blur(16px);
    padding: 7.5px 10px;
    box-shadow: 0px 3px 6px #00000029;
    & img {
        max-width: 230px;
        max-height: calc(64px - 30px);
    }

    ${desktop`
        padding: 5px 32px;
        flex-direction: row;
        align-items: center;
    `};
`;

const Logo = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
`;

const Navigation = styled.nav`
    min-width: 0;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const LoggedOutNavigationLink = styled(Link)`
    display: inline-block;
    font-size: 14px;
    font-weight: 500;
    margin-left: ${p => p.marginleft || "1vw"};
    text-decoration: none;
    padding: 9px 18px;
    color: ${p => p.color || "black"};
    align-items: flex-end;
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 8px;
    color: ${props => props.ishighlighted === "true" ? "#000000" : "white"};
    ${props => props.ishighlighted === "true" && css`
        background-color: white;
    `};

    &:hover {
        background: ${props => props.ishighlighted === "true" ? "#fff" : "rgba(255, 255, 255, 0.12)"};
    }
`;

const StoreButtonContainer = styled.div`
    text-align: right;
    align-items: center;
    z-index: 7;
    display: none;
    ${tablet`
        display: block;
    `};
`;

const StoreButton = styled.img`
    height: 39px;
    margin: 0 0.6em 0;
`;

const GuestContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    flex-direction: column;
    gap: 10px;
    ${tablet`
        flex-direction: row;
    `};
    ${desktop`
        flex-direction: row;
    `};
`;

function Header(props) {
    const userContext = useContext(UserContext);

    const isGuest = !userContext;
    const isCustomizeComplete = userContext?.[UserKeys.IS_CUSTOMIZE_COMPLETE];
    const companyBackgroundColor = isCustomizeComplete ? userContext[UserKeys.COMPANY_BACKGROUND_COLOR] : null;

    return (
        <HeaderContainer isGuest={isGuest} >
            <Content backgroundColor={companyBackgroundColor} >
                { isGuest ? <GuestHeader appVariant={props.appVariant} /> : <AuthenticatedHeader user={userContext} /> }
            </Content>
        </HeaderContainer>
    );
}

const GuestHeader = ({ appVariant }) => {
    const app = getAppDetails(appVariant);
    const shouldShowStoreLinks = appVariant !== null;
    const currentPageIs = path => {
        const location = useLocation();
        return location.pathname === path;
    };
    const linkColor = currentPageIs("/") ? "white" : null;

    return (
        <GuestContainer >
            <Logo style={{ width: "300px" }}>
                <a href="https://mebusiness.com.au" style={{ textDecoration: "none" }}>
                    <MeBusinessPortalLogo />
                </a>
            </Logo>
            <Navigation>
                { IsFeatureReady.signUpButton() && <>
                    <LoggedOutNavigationLink to="/signUp" marginleft="0" ishighlighted={currentPageIs("/signUp").toString()} color={linkColor}>
                    Sign Up
                    </LoggedOutNavigationLink>
                    <LoggedOutNavigationLink className="login" to="/login" ishighlighted={currentPageIs("/login").toString()} color={linkColor}>
                    Log In
                    </LoggedOutNavigationLink>
                
                </> }
            </Navigation>

            {shouldShowStoreLinks &&
                <StoreButtonContainer style={{ width: "300px" }}>
                    <a href={app.appStoreUrl} style={{ width: "150.82px" }}>
                        <StoreButton src={AppleImg} alt="Download on the App Store" />
                    </a>
                    <a href={app.playStoreUrl} style={{ width: "150.82px" }}>
                        <StoreButton src={GoogleImg} alt="Get it on Google Play"/>
                    </a>
                </StoreButtonContainer>
            }
        </GuestContainer>
    );
};

const AuthenticatedHeader = ({ user }) => {
    const isCustomizeComplete = user?.[UserKeys.IS_CUSTOMIZE_COMPLETE] ?? false;
    const companyLogoUrl = user?.[UserKeys.COMPANY_LOGO_URL];
    const companyLogoUpdatedAt = user?.[UserKeys.COMPANY_LOGO_UPDATED_AT];
    const shouldShowCompanyLogo = Boolean(companyLogoUrl) || isCustomizeComplete;
    const logoToShow = getDisplayableLogoUrl(companyLogoUrl, companyLogoUpdatedAt) || MeBusinessLogo;
    const fillColor = shouldShowCompanyLogo ? user[UserKeys.COMPANY_PRIMARY_COLOR] : null;
    return (
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
            { !shouldShowCompanyLogo &&
                <MeBusinessPortalLogoContainer/>
            }
            { shouldShowCompanyLogo &&
                <CompanyLogoContainer logoToShow={logoToShow} fillColor={fillColor} />
            }
        </div>
    );
};

const getDisplayableLogoUrl = (logoUrl, cacheKey) => {
    if (!logoUrl || logoUrl.startsWith("blob:") || !cacheKey) {
        return logoUrl;
    }

    const storedLogoUrl = logoUrl.split("?")[0];
    return `${storedLogoUrl}?t=${encodeURIComponent(cacheKey)}`;
};

const CompanyLogoContainer = ({ logoToShow, fillColor }) => {
    return (
        <>
            <Logo>
                <a href="https://memotivationapp.com" style={{ textDecoration: "none", color: "#FFF", display: "flex", alignItems: "center" }}>
                    <img src={logoToShow} alt="company_header_logo"></img>
                </a>
            </Logo>
            <MeBusinessPortalLogoContainer fillColor={fillColor} />
        </>
    );
};

const MeBusinessPortalLogoContainer = ({ fillColor }) => {
    return (
        <Logo>
            <a href="https://memotivationapp.com" style={{ textDecoration: "none", color: "#FFF", display: "flex", alignItems: "center" }}>
                <MeBusinessPortalLogo fillColor={fillColor} />
            </a>
        </Logo>
    );
};
export default withRouter(Header);
